/*
 * How inbox v2 names and keys its labels (design review "Label colours", Even Tone, which Dawid
 * agreed). Each label has a key, one hue at one lightness (`--lcr-label-*` in styles.css), drawn as
 * a dot inside its pill and as its rail row's dot, so the rail doubles as the list's legend. Labels
 * never use amber or gold, which are the link ink and the Send button. And one weight rule: a filled
 * pill is a lead still in play, an outlined one is closed, and a bare one with a ring is noise.
 */
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import type { InboxLabelView } from "../../src/inbox-v2/contract.ts";

export const LABEL_NAMES: Record<InboxLabelCode, string> = {
  interested: "Interested",
  question: "Question",
  not_now: "Not now",
  wrong_person: "Wrong person",
  not_interested: "Not interested",
  asked_to_stop: "Do not contact",
  auto_reply: "Auto-reply",
  not_a_sales_reply: "Not a sales reply",
};

export type LabelKey = "fern" | "cornflower" | "dusk" | "lagoon" | "poppy" | "graphite";

export const LABEL_KEYS: Record<InboxLabelCode, LabelKey> = {
  interested: "fern",
  question: "cornflower",
  not_now: "dusk",
  wrong_person: "lagoon",
  not_interested: "graphite",
  asked_to_stop: "poppy",
  auto_reply: "graphite",
  not_a_sales_reply: "graphite",
};

/** Still in play (filled), closed (outlined), or noise (bare, with a ring for its dot). */
export type LabelWeight = "alive" | "closed" | "noise";

export const LABEL_WEIGHTS: Record<InboxLabelCode, LabelWeight> = {
  interested: "alive",
  question: "alive",
  not_now: "alive",
  wrong_person: "alive",
  not_interested: "closed",
  asked_to_stop: "closed",
  auto_reply: "noise",
  not_a_sales_reply: "noise",
};

/** What lands in each label's view, as its empty state says it. */
export const LABEL_VIEW_DETAILS: Record<InboxLabelCode, string> = {
  interested: "Replies from leads who want to go ahead land here.",
  question: "Replies that ask something before the lead decides land here.",
  not_now: "Replies from leads who would talk later land here.",
  wrong_person: "Replies saying you reached the wrong person land here.",
  not_interested: "Replies from leads who said no and gave a reason land here.",
  asked_to_stop: "Replies from leads who said no, or asked you to stop writing, land here.",
  auto_reply: "Out-of-office and other automatic replies land here.",
  not_a_sales_reply: "Replies that are not about your offer land here.",
};

/** The label rows under Inbox in the rail, and the tabs below 1180px (Dawid's option B). */
export const LABEL_VIEWS: readonly { view: InboxLabelView; name: string }[] = [
  { view: "interested", name: "Interested" },
  { view: "question", name: "Questions" },
  { view: "not_now", name: "Not now" },
  { view: "wrong_person", name: "Wrong person" },
  { view: "not_a_sales_reply", name: "Other" },
];

/** Every label a rep can choose when relabelling, in the order the doc lists them. */
export const RELABEL_CHOICES: readonly InboxLabelCode[] = [
  "interested",
  "question",
  "not_now",
  "wrong_person",
  "not_interested",
  "asked_to_stop",
  "auto_reply",
  "not_a_sales_reply",
];
